import React from "react";
import swal from "sweetalert";
import { approveProfitDistribution } from "../../../../services/ProfitDistribution/approveProfitDistribution.service";
import { format } from "date-fns";
import { formatPeriod } from "../../../../helper/format_period";

// The Fund Allocation page can't be opened while a distribution is Pending
// or Rejected
const FUND_ALLOCATION_CLOSED_STATUSES = ["Pending", "Rejected"];

export const canOpenFundAllocation = (distribution) =>
  Boolean(distribution) &&
  !FUND_ALLOCATION_CLOSED_STATUSES.includes(distribution.status);

// Statuses once the fund allocation is approved (same as the server): none,
// some or all of the payments paid
export const ALLOCATION_APPROVED_STATUSES = [
  "Approved Allocated Funds",
  "Partially-Distributed",
  "Distributed",
];

export const isAllocationApproved = (distribution) =>
  ALLOCATION_APPROVED_STATUSES.includes(distribution?.status);

// Splits can be edited unless the page is closed or the allocation was
// already approved (the Cash was paid out). The server enforces the same rule.
export const canAllocateFunds = (distribution) =>
  canOpenFundAllocation(distribution) && !isAllocationApproved(distribution);

// Statuses each fund allocation decision can start from (same as the server)
const FUND_ALLOCATION_DECISION_FROM = {
  approve: ["Approved", "Rejected Allocated Funds"],
  reject: ["Approved"],
};

export const canDecideFundAllocation = (distribution, action) =>
  Boolean(distribution) &&
  FUND_ALLOCATION_DECISION_FROM[action].includes(distribution.status);

// Why Approve/Reject is unavailable, or null when it's allowed. The server
// sends `approval_block` unless the distribution date falls in an existing
// cutoff that isn't posted yet.
export const getApprovalBlockReason = (distribution) => {
  const block = distribution?.approval_block;
  if (!block) return null;

  if (block.type === "posted") {
    return `The distribution date falls in the posted cutoff "${block.cutoff.name}" (${formatPeriod(
      block.cutoff.from,
      block.cutoff.to,
    )}), so it can't be approved or rejected.`;
  }

  return `No cutoff has been created yet for the distribution date (${format(
    new Date(block.date),
    "MMM/dd/yyyy",
  )}). Create a cutoff covering this date before approving or rejecting.`;
};

const STATUS_STYLES = {
  Pending: { backgroundColor: "#fff8e6", color: "#b8860b" },
  Approved: { backgroundColor: "#e8f7ee", color: "#15803d" },
  Rejected: { backgroundColor: "#fee2e2", color: "#b91c1c" },
  Distributed: { backgroundColor: "#e6f4ff", color: "#0369a1" },
  "Partially-Distributed": { backgroundColor: "#ecfeff", color: "#0e7490" },
  "Approved Allocated Funds": { backgroundColor: "#dcfce7", color: "#166534" },
  "Rejected Allocated Funds": { backgroundColor: "#ffedd5", color: "#c2410c" },
};

const DEFAULT_STATUS_STYLE = { backgroundColor: "#eeedf8", color: "#4b44a8" };

// Colored status pill for a Profit Distribution
export const StatusPill = ({ status }) => (
  <span
    className="rounded-pill px-2 py-1 fw-semibold"
    style={{
      fontSize: "0.7rem",
      ...(STATUS_STYLES[status] || DEFAULT_STATUS_STYLE),
    }}
  >
    {status}
  </span>
);

const APPROVAL_TEXT = {
  approve: {
    confirm: (name) => `Are you sure you want to approve "${name}"?`,
    success: "Profit Distribution approved",
    successText: (name) => `"${name}" has been approved.`,
    failure: "Unable to approve the Profit Distribution.",
  },
  reject: {
    confirm: (name) =>
      `Are you sure you want to reject "${name}"? Its Retained Net Incomes will be available again for another Profit Distribution.`,
    success: "Profit Distribution rejected",
    successText: (name) =>
      `"${name}" has been rejected. Its Retained Net Incomes are available again.`,
    failure: "Unable to reject the Profit Distribution.",
  },
};

// Asks for confirmation, then approves or rejects the distribution.
// Resolves to true when the status was changed.
export const confirmApproval = async (distribution, action, userLoggedID) => {
  const text = APPROVAL_TEXT[action];

  const confirmed = await swal({
    title: "Confirm Action",
    text: text.confirm(distribution.name),
    icon: "warning",
    buttons: true,
    dangerMode: action === "reject",
  });
  if (!confirmed) return false;

  try {
    await approveProfitDistribution({
      id: distribution.id,
      action,
      userLoggedID,
    });
  } catch (error) {
    console.log(error);
    swal({
      icon: "error",
      title: "Something went wrong",
      text: error.response?.data?.message || text.failure,
    });
    return false;
  }

  swal({
    icon: "success",
    title: text.success,
    text: text.successText(distribution.name),
    timer: 2000,
    button: false,
  });
  return true;
};
